"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useCloseOnBrowserBack from "@/app/hooks/useCloseOnBrowserBack";
import { dismissAllToasts } from "@/components/design-system/toast/toastDismiss";
import CloseIcon from "public/next-public/common/icons/close-icon.svg";
import styles from "./modal.module.scss";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Body-scroll lock is ref-counted across all Modal instances so stacked modals
// only restore the original overflow once the last one closes.
let openModalCount = 0;
let storedBodyOverflow = "";

const Modal = ({
  show,
  onHide,
  title,
  children,
  footer,
  size = "medium",
  sizeDownMd,
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnBrowserBack = false,
  onBrowserForward,
  alwaysShowTitleShadow = false,
  ariaLabel,
  className,
  bodyClassName,
  id,
}: ModalProps) => {
  const [mounted, setMounted] = useState(false);
  const [bodyOverflows, setBodyOverflows] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const onHideRef = useRef(onHide);
  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  useCloseOnBrowserBack(show && closeOnBrowserBack, onHide, onBrowserForward);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!show || !mounted) {
      return;
    }
    const body = bodyRef.current;
    if (!body) {
      return;
    }
    const update = () => setBodyOverflows(body.scrollHeight > body.clientHeight + 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(body);
    if (body.firstElementChild) {
      observer.observe(body.firstElementChild);
    }
    return () => observer.disconnect();
  }, [show, mounted]);

  const handleBackdropMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnBackdrop && event.target === event.currentTarget) {
        onHideRef.current();
      }
    },
    [closeOnBackdrop],
  );

  useEffect(() => {
    if (!show) {
      return;
    }

    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = () =>
      Array.from(dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    dialog?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onHideRef.current();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const items = getFocusable();
      if (items.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    if (openModalCount === 0) {
      storedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openModalCount += 1;

    // A dialog takes the screen over from any toast behind it (SPR-2863).
    dismissAllToasts();

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      openModalCount -= 1;
      if (openModalCount === 0) {
        document.body.style.overflow = storedBodyOverflow;
      }
      previouslyFocused?.focus?.();
    };
  }, [show, mounted]);

  if (process.env.NODE_ENV !== "production" && show && !title && !ariaLabel) {
    console.warn("Modal: provide a `title` or an `ariaLabel` so the dialog has an accessible name.");
  }

  if (!mounted || !show) {
    return null;
  }

  return createPortal(
    <div className={styles.Modal} onMouseDown={handleBackdropMouseDown}>
      <div
        ref={dialogRef}
        id={id}
        className={`${styles.dialog} ${styles[size]} ${sizeDownMd ? styles.sizeDownMdMedium : ""} ${bodyOverflows ? styles.bodyOverflows : ""} ${alwaysShowTitleShadow ? styles.titleShadow : ""} ${className ?? ""}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
      >
        {(title || showCloseButton) && (
          <div className={styles.title}>
            <div id={titleId} className={styles.titleText}>
              {title}
            </div>
            {showCloseButton && (
              <button type="button" className={styles.closeButton} onClick={onHide} aria-label="Close">
                <CloseIcon width={16} height={16} aria-hidden />
              </button>
            )}
          </div>
        )}
        <div ref={bodyRef} className={`${styles.body} ${bodyClassName ?? ""}`.trim()}>
          {children}
        </div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

Modal.displayName = "Modal";

export default Modal;

export type ModalSize = "skinny" | "medium" | "full";

export interface ModalProps {
  show: boolean;
  onHide: () => void;
  title?: React.ReactNode;
  /** Carries its own padding — the body wrapper is padding-free. */
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  /** Below the md breakpoint (<768px), cap the dialog at the Medium width instead of `size`. */
  sizeDownMd?: "medium";
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  /** The browser Back button closes the modal instead of leaving the page. */
  closeOnBrowserBack?: boolean;
  /** With closeOnBrowserBack, called when Forward returns to the modal after it closed, to reopen it. */
  onBrowserForward?: () => void;
  /** Shadow under the title bar even when the body doesn't scroll. */
  alwaysShowTitleShadow?: boolean;
  /** Accessible name used when no visible `title` is provided. */
  ariaLabel?: string;
  className?: string;
  bodyClassName?: string;
  id?: string;
}
